import {
  GetInstallationDecisionsDocument,
  GetInstallationDecisionsQuery,
  SaveDecision_Input,
  SaveDecisionMutation,
  useGetDecisionDetailQuery,
  useSaveDecisionMutation
} from 'generated/graphql';
import _ from 'lodash';
import { DateTime } from 'luxon';
import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';

export type IFormState = {
  id: string | null;
  approved: string;
  dueDate: string;
  installationId: string;
};

const initialState: IFormState = {
  id: null,
  approved: '',
  dueDate: '',
  installationId: ''
};

export function useFormState(id: string) {
  const {
    formState,
    dispatch,
    genFieldProps
  } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      id: {
        label: 'id',
        type: 'text'
      },
      approved: {
        label: 'approved',
        type: 'text'
      },
      dueDate: {
        label: 'Due Date',
        type: 'date'
      }
    }
  });

  const { data, called, loading } = useQuery(id);

  useEffect(() => {
    if (!loading && data.id !== formState.id && called && formState.id === null) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          id: data.id,
          approved: data.approved,
          dueDate: data.dueDate,
          installationId: ''
        }
      });
    }


  }, [
    called,
    data,
    dispatch,
    formState.id,
    loading
  ]);

  return React.useMemo(
    () => {


      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}


export interface IData {
  id: string;
  title: string;
  approved: string;
  dueDate: string;
}

export function useQuery(id: string) {
  const tuple = useGetDecisionDetailQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {

        id: tuple.data?.decision?.id ?? '',
        title: tuple.data?.decision?.title ?? '',
        approved: tuple.data?.decision?.approved === true ? 'true' : tuple.data?.decision?.approved === false ? 'false' : '',
        dueDate: DateTime.fromISO(`${tuple.data?.decision?.dueDate}`).toFormat('yyyy-LL-dd') ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}

export function useSaveDecision() {
  const [
    fnMutate,
    tuple
  ] = useSaveDecisionMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (input: SaveDecision_Input) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<GetInstallationDecisionsQuery, SaveDecisionMutation>(GetInstallationDecisionsDocument, (prevState, { mutationResult }) => {
            if (!prevState.installation?.decisions) {
              return prevState;
            }
            if (!mutationResult.data?.saveDecision?.id) {
              return prevState;
            }

            const existing = prevState.installation.decisions.find((node) => node.id === mutationResult.data?.saveDecision?.id);
            if (!existing) {
              return {
                ...prevState,
                installation: {
                  ...prevState.installation,
                  decisions: _.compact([
                    ...prevState.installation.decisions,
                    mutationResult.data.saveDecision
                  ])
                }
              };
            } else {
              const updatedDecision = prevState.installation.decisions.map((node) => {
                if (node.id === mutationResult.data?.saveDecision?.id) {
                  return {
                    ...node,
                    ...mutationResult.data.saveDecision
                  };
                } else return {
                  ...node
                };
              });
              return {
                ...prevState,
                installation: {
                  ...prevState.installation,
                  decisions: updatedDecision
                }
              };
            }
          })
        }
      });
    },
    [fnMutate]
  );


  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
